import clsx from 'clsx';
import React, { useEffect } from 'react';
import styled from 'styled-components';
import { UiGrid, UiImage, UiText, UiButton, UiFlex } from '@open-stack/ui';
import { XInputPhone } from '../../components/x/input/phone/XInputPhone';
import { useApiQuery } from '@orderx/lab/xr/http';
import { apiSpcPublicConnectPost } from '../../api/spc.public.Connect.[post]';
import { useImmer } from 'use-immer';
import { toastShowError, toastShowSuccess } from '@orderx/lab/xr/toast';
import { useMe } from '../../hooks/me';
import { AppCache } from '../../services/app-cache';
import moment from 'moment';
import { useMeta } from '../../hooks/meta';
import { XButton, XButtonProps } from '../../components/x/button/XButton';
import { XText } from '../../components/x/text/XText';
import { XInput } from '../../components/x/input/XInput';
import { useDeviceFingerprint } from '../../hooks/device-fingerprint';

const Wrapper = styled.div`
    display: grid;
    align-content: center;
    background-color: var(--x-color-body);
    position: fixed;
    height: 100%;
    width: 100%;
    top: 0;
    left: 0;
    z-index: var(--z-index-connect);
    text-align: center;

    .fp {
        position: absolute;
        bottom: 0;
        left: 50%;
        transform: translate(-50%, -16px);
        font-size: 12px;
        opacity: 0.5;
    }

    img {
        margin: 0 auto;
    }

    .input-container {
        background-color: var(--x-color-surface);
    }

    .confirmation-code {
        text-align: center;
        letter-spacing: 10px;
    }

    .back-button {
        position: fixed;
        top: var(--ui-spacing);
        left: var(--ui-spacing);
        z-index: 1;
    }
`;

enum ActiveView {
    LOGIN = 'LOGIN',
    REGISTER = 'REGISTER',
    FORGOT_PASSWORD = 'FORGOT_PASSWORD',
    FORGOT_PASSWORD_FINISH = 'FORGOT_PASSWORD_FINISH',
    CONFIRM = 'CONFIRM',
}

interface SpcState {
    areaCode: string;
    phone: string;
    password: string;
    activeView: ActiveView;
    isPasswordInputVisible: boolean;
    firstName: string;
    confirmationCode: string;
    confirmCodeResendTime: string;
    confirmCodeResendLabel: string;
}

export function SpcConnect({ onBack }: { onBack?(): void }) {
    const { setToken, loadUser } = useMe();

    const { areaCode } = useMeta().meta;

    const [state, updateState] = useImmer<SpcState>({
        areaCode: AppCache.get('previousLoginData')?.areaCode ?? areaCode,
        phone: AppCache.get('previousLoginData')?.phone ?? '',
        password: '',
        activeView: ActiveView.LOGIN,
        isPasswordInputVisible: Boolean(AppCache.get('previousLoginData')),
        firstName: '',
        confirmationCode: '',
        confirmCodeResendTime: '',
        confirmCodeResendLabel: 'Resend confirmation code',
    });

    useEffect(() => {
        updateState((draft) => {
            draft.password = '';
        });
    }, [state.activeView]);

    useEffect(() => {
        if (state.confirmCodeResendTime) {
            const interval = setInterval(() => {
                const confirmCodeSentAt = moment(state.confirmCodeResendTime);
                let secondsUntilReset = confirmCodeSentAt.add(60, 'seconds').diff(moment(), 'seconds');

                secondsUntilReset = secondsUntilReset <= 0 ? 0 : secondsUntilReset;

                updateState((draft) => {
                    if (secondsUntilReset > 0) {
                        draft.confirmCodeResendLabel = `You can resend another one in ${secondsUntilReset} seconds`;
                    } else {
                        draft.confirmCodeResendLabel = 'Resend confirmation code';
                        draft.confirmCodeResendTime = '';
                    }
                });
            }, 250);

            return () => clearInterval(interval);
        }
    }, [state.confirmCodeResendTime]);

    const apiQueryBootstrap = useApiQuery(() => apiSpcPublicConnectPost({}));

    const apiQueryAuthenticate = useApiQuery(async (body) => {
        const response = await apiSpcPublicConnectPost({ body });

        if (response.action === 'REQUEST_PASSWORD') {
            updateState((draft) => {
                draft.isPasswordInputVisible = true;
            });
        }

        if (response.action === 'AUTHENTICATE_FAILED') {
            toastShowError('Failed to authenticate');
        }

        if (response.action === 'CONFIRM_FAILED') {
            toastShowError('Failed to confirm');
        }

        if (response.action === 'CONFIRM_CODE_RESENT') {
            toastShowSuccess('New confirmation code has been sent');

            updateState((draft) => {
                draft.confirmCodeResendTime = moment().format();
            });
        }

        if (response.action === 'USER_EXISTS') {
            toastShowError('Account already exists');
        }

        if (response.action === 'FORGOT_PASSWORD_REQUEST_FAILED') {
            toastShowError('Failed to request password change');
        }

        if (response.action === 'REGISTRATION_FAILED') {
            toastShowError('Something went wrong');
        }

        if (response.action === 'FORGOT_PASSWORD_REQUEST_SENT') {
            updateState((draft) => {
                draft.activeView = ActiveView.FORGOT_PASSWORD_FINISH;
            });
        }

        if (response.action === 'CONFIRM_REQUIRED') {
            updateState((draft) => {
                draft.activeView = ActiveView.CONFIRM;
            });
        }

        if (response.action === 'AUTHENTICATED' || response.action === 'CONFIRM_SUCCESS') {
            AppCache.set('previousLoginData', {
                areaCode: response.areaCode,
                phone: response.phone,
            });

            setToken(response.token);
            await loadUser();
        }

        return response;
    });

    useEffect(() => {
        apiQueryBootstrap.run();
    }, []);

    if (!apiQueryBootstrap.response) {
        return null;
    }

    return (
        <Index state={state} updateState={updateState} apiQueryAuthenticate={apiQueryAuthenticate} onBack={onBack} />
    );
}

function Index({
    state,
    updateState,
    apiQueryAuthenticate,
    onBack,
}: {
    state: SpcState;
    updateState(callback: (draft: SpcState) => void): void;
    apiQueryAuthenticate: any;
    onBack?(): void;
}) {
    const { deviceFingerprint } = useDeviceFingerprint();
    const { areaCode, phone, password, activeView, isPasswordInputVisible, firstName, confirmationCode } = state;

    const isValidPhone = areaCode && phone.length > 5;
    const isValidConfirmationCode = confirmationCode?.length === 6;
    const isValidPassword = password?.length > 3;

    const elementLogo = <UiImage src="https://orderx-images.s3-eu-west-1.amazonaws.com/orderx-logo.png" height={24} />;

    const ctaButtonProps: XButtonProps = {
        color: 'primary',
        isLoading: apiQueryAuthenticate.isLoading,
        cta: true,
    };

    const elementPhone = (
        <XInputPhone
            areaCode={areaCode}
            phone={phone}
            onChange={({ areaCode, phone }) => {
                updateState((draft) => {
                    draft.areaCode = areaCode;
                    draft.phone = phone;
                });
            }}
        />
    );

    const elementConfirmationCode = (
        <XInput
            className={clsx(confirmationCode && 'confirmation-code')}
            value={confirmationCode}
            placeholder="Enter 6-digit confirmation code"
            maxLength={6}
            onChangeValue={(confirmationCode) => {
                updateState((draft) => {
                    draft.confirmationCode = confirmationCode;
                });
            }}
        />
    );

    const elementPassword = (
        <XInput
            type="password"
            value={password}
            placeholder="Enter password"
            onChangeValue={(password) => {
                updateState((draft) => {
                    draft.password = password;
                });
            }}
            autoComplete={'off'}
        />
    );

    function createBackButton(onClick: () => void) {
        return <XButton onClick={onClick} className="back-button" icon="arrow_back" variant="text" />;
    }

    return (
        <Wrapper className={clsx('SpcConnect')}>
            <UiGrid padding={4} paddingVertical={8} gap={4}>
                {(activeView === ActiveView.LOGIN && (
                    <>
                        {onBack && createBackButton(() => onBack())}

                        {elementLogo}

                        <XText>Sign in with your mobile number</XText>

                        <UiGrid>
                            {elementPhone}
                            {isPasswordInputVisible && elementPassword}
                        </UiGrid>

                        <XButton
                            onClick={() =>
                                apiQueryAuthenticate.run({
                                    action: 'AUTHENTICATE',
                                    areaCode,
                                    phone,
                                    password,
                                })
                            }
                            {...ctaButtonProps}
                            disabled={!isValidPhone}>
                            Next
                        </XButton>

                        <UiFlex gap={1} flexChildren>
                            <UiButton
                                onClick={() =>
                                    updateState((draft) => {
                                        draft.activeView = ActiveView.REGISTER;
                                    })
                                }
                                size={-1}
                                variant="text">
                                Create an account
                            </UiButton>

                            <UiButton
                                onClick={() =>
                                    updateState((draft) => {
                                        draft.activeView = ActiveView.FORGOT_PASSWORD;
                                    })
                                }
                                variant="text"
                                size={-1}>
                                Forgot password?
                            </UiButton>
                        </UiFlex>
                    </>
                )) ||
                    (activeView === ActiveView.REGISTER && (
                        <>
                            {createBackButton(() => {
                                updateState((draft) => {
                                    draft.activeView = ActiveView.LOGIN;
                                });
                            })}

                            {elementLogo}

                            <XText>Sign up for your account</XText>

                            <UiGrid>
                                {elementPhone}

                                <XInput
                                    type="password"
                                    value={password}
                                    placeholder="Create password"
                                    autoComplete="off"
                                    onChangeValue={(password) => {
                                        updateState((draft) => {
                                            draft.password = password;
                                        });
                                    }}
                                />

                                <XInput
                                    value={firstName}
                                    placeholder="Enter first name"
                                    onChangeValue={(firstName) => {
                                        updateState((draft) => {
                                            draft.firstName = firstName;
                                        });
                                    }}
                                />
                            </UiGrid>

                            <XButton
                                onClick={() =>
                                    apiQueryAuthenticate.run({
                                        action: 'REGISTER',
                                        areaCode,
                                        phone,
                                        password,
                                        firstName,
                                    })
                                }
                                {...ctaButtonProps}
                                disabled={!isValidPhone || !isValidPassword || firstName?.length < 2}>
                                Next
                            </XButton>
                        </>
                    )) ||
                    (activeView === ActiveView.CONFIRM && (
                        <>
                            {createBackButton(() => {
                                updateState((draft) => {
                                    draft.activeView = ActiveView.LOGIN;
                                });
                            })}

                            {elementLogo}

                            <XText>
                                Confirm your account by entering the 6-digit code you should have received on your
                                phone: +{state.areaCode}
                                {state.phone}
                            </XText>

                            <UiGrid>{elementConfirmationCode}</UiGrid>

                            <XButton
                                onClick={() =>
                                    apiQueryAuthenticate.run({
                                        action: 'CONFIRM',
                                        areaCode,
                                        phone,
                                        confirmationCode,
                                    })
                                }
                                {...ctaButtonProps}
                                disabled={!isValidConfirmationCode}>
                                Next
                            </XButton>

                            <UiText
                                onClick={() => {
                                    if (!state.confirmCodeResendTime) {
                                        apiQueryAuthenticate.run({
                                            action: 'CONFIRM_CODE_RESEND',
                                            areaCode,
                                            phone,
                                        });
                                    }
                                }}
                                fontSize={-1}>
                                {state.confirmCodeResendLabel}
                            </UiText>
                        </>
                    )) ||
                    (activeView === ActiveView.FORGOT_PASSWORD && (
                        <>
                            {createBackButton(() => {
                                updateState((draft) => {
                                    draft.activeView = ActiveView.LOGIN;
                                });
                            })}

                            {elementLogo}

                            <XText>Enter your phone number</XText>

                            <UiGrid>{elementPhone}</UiGrid>

                            <XButton
                                onClick={() =>
                                    apiQueryAuthenticate.run({
                                        action: 'FORGOT_PASSWORD_REQUEST',
                                        areaCode,
                                        phone,
                                    })
                                }
                                {...ctaButtonProps}
                                disabled={!isValidPhone}>
                                Next
                            </XButton>
                        </>
                    )) ||
                    (activeView === ActiveView.FORGOT_PASSWORD_FINISH && (
                        <>
                            {createBackButton(() => {
                                updateState((draft) => {
                                    draft.activeView = ActiveView.LOGIN;
                                });
                            })}

                            {elementLogo}

                            <XText>
                                Enter the 6-digit you should have received on your phone: +{state.areaCode}
                                {state.phone}
                            </XText>

                            <UiGrid>
                                {elementConfirmationCode}

                                <XText>Then choose a new password</XText>

                                {elementPassword}
                            </UiGrid>

                            <XButton
                                onClick={() =>
                                    apiQueryAuthenticate.run({
                                        action: 'FORGOT_PASSWORD_FINISH',
                                        areaCode,
                                        phone,
                                        confirmationCode,
                                        password,
                                    })
                                }
                                {...ctaButtonProps}
                                disabled={!isValidConfirmationCode || !isValidPassword}>
                                Next
                            </XButton>
                        </>
                    )) ||
                    null}
            </UiGrid>

            <div onClick={() => {}} className={'fp'}>
                {deviceFingerprint || ''}
            </div>
        </Wrapper>
    );
}
